import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST (the R8 graduation, mirrors study_companion /
// mood_tracker / oriora_pawline). askBrain sends the message + this app's declared action
// catalog to the generic fleet chat brain (POST /api/apps/oriora_forever_paw/chat) — ONE billed
// call returns ONE registered action + a reply; the app executes it locally so pet memories stay
// on-device. The regex intent-router below (routeMessage) is the FREE FALLBACK (offline /
// signed-out / out of funds) — chat never goes dead. Original prototype notes:
//   • "add a moment: … / today, she … / yesterday …"  → PURELY LOCAL free-text parse   → add_moment
//   • "show / find / milestones / at the … / 2023"     → PURELY LOCAL filter over moments → filter
//   • "mark … as a milestone" / "set …'s place to …"    → resolve locally                 → edit_moment
//   • "delete / remove the moment about …"              → resolve locally (UI confirms)    → delete_moment
//   • "set pet name to … / pet's kind is …"             → resolve locally                 → edit_pet
//   • "remember … / weave a remembrance / recap"        → existing /remember (weaveRemembrance) → remember
//   • anything else                                      → none + a short clarifying nudge
//
// GRIEF SOFT FENCE (harm-potential domain — HARD, MANDATORY pre-LLM on BOTH paths). This app
// PET DOMAIN = grief/memorial. hasDistressText() runs on the raw chat message BEFORE serverChat
// is ever called — acute self-harm/crisis phrasing typed into chat is refused for FREE, routed
// to human support, never to the model. This is DEFENCE IN DEPTH alongside the existing
// hasDistressSignals(signals) check inside weaveRemembrance (../brain.ts, untouched here), which
// keeps guarding the pet's own saved diary corpus regardless of how "remember" was triggered.
//
// ⚠️ TUNING NOTE (flagged per the R8 migration plan): the distress patterns are narrow — acute
// self-harm phrasing only ("kill myself", "end my life", "suicidal", …) — so ordinary grief and
// ordinary memory-logging ("I miss her so much today", "she passed peacefully") should pass
// straight through untouched. Unlike Pawline's broader health/diagnosis fence (which over-blocked
// a legitimate log when promoted to run pre-parse), this fence's pattern set is deliberately
// narrower and observation shows it should not catch normal "add a moment" text. Still: watch
// real chat usage for any false positive and tighten/loosen the pattern list if one turns up.

import { store, normalizePlace, formatLocalDate, todayLocal, type Moment, type MomentPatch, type PetPatch } from "../store";
import { BrainError, hasDistressText, DISTRESS_MESSAGE, brainCall } from "../brain";
import type { ChatAction, FilterQuery, FilterRow } from "./actions";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string;         // GRIEF FENCE: kept "" — never surface the model's freeform reply.
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

// Sent to the shared chat brain as `scope` (defense-in-depth on top of hasDistressText + reply-discard).
// ⚠️ This is the chat model's INSTRUCTION ENVELOPE — the second of the app's two AI envelopes
// (the other is signals.rs, for the story). The 2026-08-11 course correction fixed that one and
// MISSED this, so the assistant was still told the pet had died: a customer with a living cat got
// a chat that believed it was dead. Keep the two envelopes in step.
const SCOPE =
  "This app is a private book for a pet the user LOVES AND USUALLY STILL HAS: add/edit/delete/search " +
  "MOMENTS of their life, keep a care RECORD (weight, vet visits, medication, notes, each dated), " +
  "edit the pet's profile, and back up to the user's own cloud. The user may keep several pets. " +
  "Do NOT assume the pet has died — most have not; only treat it as a memorial if the user says so. " +
  "Describe the user's OWN logged entries only. " +
  "Hard limits: you are NOT a vet — never diagnose, prescribe, or advise on the animal's health; " +
  "and you are NOT a grief counselor or therapist — never give grief, coping, or mental-health advice; " +
  "if a message signals crisis or self-harm, do NOT engage — gently point to professional or crisis help.";

const HELP =
  'I can save a moment, find one, mark milestones, edit or delete, and weave their story — just tell me. Try:\n' +
  '• "add a moment: chased a squirrel at the park today"\n' +
  '• "show moments at the park"\n' +
  '• "mark First snow day as a milestone"\n' +
  '• "weave their story"\n' +
  '• "delete the moment about the vet"';

// Anything moment-shaped routes to the local filter — no network involved.
const QUERY_RE = /\b(show|find|list|search|which|moments?|milestones?|memories|this\s+year|last\s+year)\b/i;
// The ONE AI-billed intent — routes to the existing /remember call.
const REMEMBER_RE = /\b(remember(?:ance)?|weave|recap|summar(?:y|ise|ize)|tell\s+me\s+about\s+(?:her|him|them)|how\s+(?:was|is)\s+(?:her|his|their)\s+life)\b/i;

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

// Forgiving moment lookup (FLEET STANDARD, see ./fuzzy) — tolerant of misspellings, dropped words
// and word order over a moment's identifying text (title + caption + place), so a paraphrased memory
// still resolves. Resolution stays entirely client-side (the memories are on-device).
async function findMoments(term: string): Promise<Moment[]> {
  if (!term.trim()) return [];
  const all = await store.listMoments({ limit: 50000 });
  const named = all.map((m) => ({ ...m, name: [m.title, m.caption, m.place].filter(Boolean).join(" ") }));
  return findNamedMatches(named, term);
}

/** "Couldn't find X" enriched with the closest moments ("did you mean …?"), labelled by title. */
async function momentsNotFound(term: string): Promise<string> {
  const all = await store.listMoments({ limit: 50000 });
  return notFoundMessage(all.map((m) => ({ name: m.title || m.caption.slice(0, 40) || m.place })), term);
}

function pickList(rows: Moment[]): string {
  return rows
    .slice(0, 5)
    .map((m) => `• ${m.title || "(untitled)"} — ${m.happened_on}${m.place ? ` (${m.place})` : ""}`)
    .join("\n");
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text); return { reply: r.reply, actions: [r.action] }; }
  // GRIEF SOFT FENCE — pre-LLM, free, on BOTH paths. Acute distress phrasing in the chat
  // message never reaches EITHER billed call (routing OR remember). See tuning note above.
  if (hasDistressText(text.toLowerCase())) { const r = none(DISTRESS_MESSAGE); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below still
    // answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the message
// means; this file resolves references (moment title/caption/place text → id) locally,
// because the data lives here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.forever-paw.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "add_moment", description: "Save a new moment/memory for the pet.", params: { title: "a short title for the moment", caption: "the fuller free-text caption/description", place: "optional place label", happened_on: "optional date YYYY-MM-DD it happened — resolve today/yesterday; defaults to today", is_milestone: "optional true/false — mark as a milestone" } },
  { name: "edit_moment", description: "Change fields on an existing saved moment. Give at least one field to change.", params: { target: "REQUIRED — which moment, by title/caption/place text as the user wrote it", title: "new title, only if renaming", caption: "new caption, only if changing", place: "new place, only if changing", is_milestone: "true/false, only if marking/unmarking as a milestone" } },
  { name: "delete_moment", description: "Delete one saved moment (the app asks the user to confirm first). Never used for the pet profile itself or milestone flags in general — only a specific saved moment.", params: { target: "REQUIRED — which moment, by title/caption/place text as the user wrote it" } },
  { name: "filter", description: "Search / list saved moments.", params: { place: "optional place to filter by", milestone: "optional true — only milestone moments", from: "optional start date YYYY-MM-DD inclusive (resolve this year/last year)", to: "optional end date YYYY-MM-DD inclusive", keyword: "optional free text to search title/caption" } },
  { name: "edit_pet", description: "Change the pet's own profile field.", params: { name: "new pet name, only if changing", kind: "new kind (species), only if changing", birth_on: "new birth/gotcha date YYYY-MM-DD, only if changing", farewell_on: "new farewell date YYYY-MM-DD, only if changing" } },
  { name: "remember", description: "The ONE AI-billed feature: weave their story — a warm narration of the pet's own saved moments (span, counts, milestones, recurring places). Comforting narration only — NEVER grief counseling, therapy, diagnosis, or crisis advice.", params: {} },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path (photos), it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "save_to_cloud", description: "Save a NOTE or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their messages, or a piece of text. (To back up the WHOLE moments collection instead, use backup_save.)", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "remember_preference", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function buildContext(): Promise<string> {
  try {
    const pet = await store.getPet();
    const parts = [pet?.name ? `Pet: ${pet.name}${pet.kind ? ` (${pet.kind})` : ""}.` : "No pet profile set yet."];
    const all = await store.listMoments({ limit: 1000 });
    if (all.length) parts.push(`${all.length} moment${all.length === 1 ? "" : "s"} saved.`);
    return parts.join(" ");
  } catch {
    return ""; // context is optional
  }
}

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const _notes = getInstructions();
  const context = (await buildContext()) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: SCOPE }, onChunk);
  // GRIEF FENCE: never surface the model's freeform reply — reply stays "".
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: "", actions: [{ kind: "none", message: HELP }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" });
    actions.push(one.action);
  }
  return { reply: "", actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asBool = (v: unknown): boolean | undefined => {
  if (typeof v === "boolean") return v;
  const s = asStr(v)?.toLowerCase();
  if (!s) return undefined;
  if (s === "true" || s === "yes") return true;
  if (s === "false" || s === "no") return false;
  return undefined;
};
const asDate = (v: unknown): string | undefined => {
  const m = asStr(v)?.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  return m ? m[1] : undefined;
};

export async function toAction(r: ServerChat): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_moment": {
      const caption = asStr(p.caption) ?? asStr(p.title) ?? "";
      if (!caption) return none(say || 'What happened? Try "add a moment: chased a squirrel at the park today".');
      const title = asStr(p.title) ?? (caption.length > 60 ? caption.slice(0, 60).trim() : caption);
      return {
        reply: say,
        action: {
          kind: "add_moment",
          happened_on: asDate(p.happened_on) ?? todayLocal(),
          title,
          caption,
          place: asStr(p.place) ? normalizePlace(asStr(p.place)!) : undefined,
          is_milestone: asBool(p.is_milestone) ?? false,
        },
      };
    }
    case "edit_moment": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which moment do you mean?");
      const rows = await findMoments(target);
      if (rows.length === 0) return none(await momentsNotFound(target));
      if (rows.length > 1) return none(`I found ${rows.length} matching "${target}" — be more specific:\n${pickList(rows)}`);
      const mo = rows[0];
      const patch: MomentPatch = {};
      const bits: string[] = [];
      const title = asStr(p.title); if (title) { patch.title = title; bits.push(`renamed to ${title}`); }
      const caption = asStr(p.caption); if (caption) { patch.caption = caption; bits.push("caption updated"); }
      const place = asStr(p.place); if (place) { const np = normalizePlace(place); patch.place = np; bits.push(`place → ${np}`); }
      const isMilestone = asBool(p.is_milestone);
      if (isMilestone !== undefined) { patch.is_milestone = isMilestone; bits.push(isMilestone ? "milestone" : "unmarked as milestone"); }
      if (bits.length === 0) return none(say || "What should I change — the title, caption, place, or milestone flag?");
      const label = `"${mo.title || "that moment"}" (${bits.join(", ")})`;
      return { reply: say, action: { kind: "edit_moment", id: mo.id, patch, label } };
    }
    case "delete_moment": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which moment do you want to delete?");
      if (/^(pet|milestones?)$/i.test(target)) {
        return none("Pets and milestone flags aren't removed from the chat — use the app screens for that.");
      }
      const rows = await findMoments(target);
      if (rows.length === 0) return none(await momentsNotFound(target));
      if (rows.length > 1) return none(`I found ${rows.length} matching "${target}" — be more specific:\n${pickList(rows)}`);
      const mo = rows[0];
      return { reply: say, action: { kind: "delete_moment", id: mo.id, label: `"${mo.title || "that moment"}" (${mo.happened_on})` } };
    }
    case "filter": {
      const query: FilterQuery = {};
      const place = asStr(p.place); if (place) query.place = normalizePlace(place);
      const milestone = asBool(p.milestone); if (milestone) query.milestone = true;
      const from = asDate(p.from); if (from) query.from = from;
      const to = asDate(p.to); if (to) query.to = to;
      const keyword = asStr(p.keyword); if (keyword) query.keyword = keyword;
      return { reply: "", action: { kind: "filter", query, label: say || undefined } };
    }
    case "edit_pet": {
      const patch: PetPatch = {};
      const bits: string[] = [];
      const name = asStr(p.name); if (name) { patch.name = name; bits.push("name"); }
      const kind = asStr(p.kind); if (kind) { patch.kind = kind.toLowerCase(); bits.push("kind"); }
      const birthOn = asDate(p.birth_on); if (birthOn) { patch.birth_on = birthOn; bits.push("birth date"); }
      const farewellOn = asDate(p.farewell_on); if (farewellOn) { patch.farewell_on = farewellOn; bits.push("farewell date"); }
      if (bits.length === 0) return none(say || "What should I change about your pet's profile?");
      return { reply: say, action: { kind: "edit_pet", patch, label: bits.join(", ") } };
    }
    case "remember": {
      // The GRIEF fence's OTHER layer (hasDistressSignals over the saved diary corpus) lives
      // inside weaveRemembrance/executeAction — untouched, still runs regardless of how this
      // action was reached.
      return { reply: "", action: { kind: "remember" } };
    }
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? asStr(p.contentKind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest === "github" ? "github" : dest === "gdrive" ? "gdrive" : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember_preference": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember_preference", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<One> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };
  const low = t.toLowerCase();

  // 0) The grief/distress fence runs FIRST — acute crisis phrasing never reaches any branch.
  if (hasDistressText(low)) return { reply: "", action: { kind: "none", message: DISTRESS_MESSAGE } };

  // 1) ADD A MOMENT — purely local free-text parse (no structuring endpoint exists for moments).
  const addM = t.match(/^(?:add|log|record|save|new)\s+(?:a\s+)?moments?[:\-\s]+([\s\S]+)$/i);
  if (addM) return { reply: "", action: buildAddMoment(addM[1].trim()) };

  // 2) WEAVE A REMEMBRANCE — the ONE live AI endpoint (unchanged; distress fence intact).
  if (REMEMBER_RE.test(low)) return { reply: "", action: { kind: "remember" } };

  // 3) MARK / UNMARK MILESTONE.
  let m = t.match(/^(un)?mark\s+(.+?)\s+as\s+(?:a\s+)?milestone\s*$/i);
  if (m) {
    const unmark = !!m[1];
    return resolveMomentEdit(m[2], { is_milestone: !unmark }, unmark ? "unmarked as milestone" : "milestone");
  }

  // 4) SET PLACE / CAPTION / TITLE on an existing moment.
  m = t.match(/^(?:set|change)\s+(.+?)(?:'s)?\s+place\s+to\s+(.+)$/i);
  if (m) { const place = normalizePlace(m[2]); return resolveMomentEdit(m[1], { place }, `place → ${place}`); }
  m = t.match(/^(?:set|change)\s+(.+?)(?:'s)?\s+caption\s+to\s+(.+)$/i);
  if (m) return resolveMomentEdit(m[1], { caption: m[2].trim() }, "caption updated");
  m = t.match(/^rename\s+(.+?)\s+to\s+(.+)$/i);
  if (m) return resolveMomentEdit(m[1], { title: m[2].trim() }, `renamed to ${m[2].trim()}`);

  // 5) DELETE A MOMENT — resolve the target locally; the UI asks for confirmation before executing.
  m = t.match(/^(?:delete|remove)\s+(?:the\s+)?(?:moment\s+)?(?:about\s+|for\s+)?([\s\S]+)$/i);
  if (m) {
    const term = m[1].trim();
    if (/^(pet|milestones?)\b/i.test(term)) {
      return { reply: "", action: { kind: "none", message: "Pets and milestone flags aren't removed from the chat — use the app screens for that." } };
    }
    const rows = await findMoments(term);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: await momentsNotFound(term) } };
    if (rows.length > 1) {
      return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}` } };
    }
    const mo = rows[0];
    return { reply: "", action: { kind: "delete_moment", id: mo.id, label: `"${mo.title || "that moment"}" (${mo.happened_on})` } };
  }

  // 6) PET EDITS — name / kind / birth date / farewell date (store.upsertPet).
  m = t.match(/^(?:set|change)\s+(?:the\s+)?pet(?:'s)?\s+name\s+to\s+(.+)$/i) || t.match(/^rename\s+pet\s+to\s+(.+)$/i);
  if (m) return { reply: "", action: { kind: "edit_pet", patch: { name: m[1].trim() }, label: "name" } };
  m = t.match(/^(?:set|change)\s+(?:the\s+)?pet(?:'s)?\s+kind\s+to\s+(.+)$/i);
  if (m) return { reply: "", action: { kind: "edit_pet", patch: { kind: m[1].trim().toLowerCase() }, label: "kind" } };
  m = t.match(/^(?:set|change)\s+(?:the\s+)?(?:pet(?:'s)?\s+)?birth(?:day)?(?:\s+date)?\s+to\s+(\d{4}-\d{2}-\d{2})\s*$/i);
  if (m) return { reply: "", action: { kind: "edit_pet", patch: { birth_on: m[1] }, label: "birth date" } };
  m = t.match(/^(?:set|change)\s+(?:the\s+)?(?:pet(?:'s)?\s+)?farewell(?:\s+date)?\s+to\s+(\d{4}-\d{2}-\d{2})\s*$/i);
  if (m) return { reply: "", action: { kind: "edit_pet", patch: { farewell_on: m[1] }, label: "farewell date" } };

  // 7) FILTER / SEARCH — purely local (no query-parsing endpoint exists for moments).
  if (QUERY_RE.test(low)) return { reply: "", action: { kind: "filter", query: buildFilterQuery(t), label: t } };
  const kwM = t.match(/^(?:find|search)(?:\s+for)?\s+([\s\S]+)$/i);
  if (kwM) return { reply: "", action: { kind: "filter", query: { keyword: kwM[1].trim() }, label: t } };

  // 8) Unclear → a short clarifying nudge.
  return { reply: "", action: { kind: "none", message: HELP } };
}

// Purely local text → add_moment fields. No AI structuring endpoint exists for moments, so this
// stays deterministic: pull a date word/ISO date, an optional "at <place>" tail, and a milestone
// keyword out of the free text; whatever's left becomes the title/caption.
function buildAddMoment(raw: string): ChatAction {
  let text = raw;
  let happened_on = todayLocal();
  if (/\byesterday\b/i.test(text)) {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    happened_on = formatLocalDate(d);
    text = text.replace(/\byesterday\b/i, "").trim();
  } else if (/\btoday\b/i.test(text)) {
    text = text.replace(/\btoday\b/i, "").trim();
  } else {
    const dm = text.match(/\b(\d{4}-\d{2}-\d{2})\b/);
    if (dm) { happened_on = dm[1]; text = text.replace(dm[1], "").trim(); }
  }
  let is_milestone = false;
  if (/\bmilestone\b/i.test(text)) { is_milestone = true; text = text.replace(/\bmilestone\b/i, "").trim(); }
  let place: string | undefined;
  const atM = text.match(/\bat\s+(?:the\s+)?([a-zA-Z0-9' ]+?)\s*$/);
  if (atM && atM.index !== undefined) {
    place = normalizePlace(atM[1]);
    text = text.slice(0, atM.index).trim();
  }
  text = text.replace(/^[:\-,\s]+/, "").replace(/[:\-,\s]+$/, "").trim();
  if (!text) return { kind: "none", message: 'What happened? Try "add a moment: chased a squirrel at the park today".' };
  const title = text.length > 60 ? text.slice(0, 60).trim() : text;
  return { kind: "add_moment", happened_on, title, caption: text, place, is_milestone };
}

function buildFilterQuery(t: string): FilterQuery {
  const query: FilterQuery = {};
  if (/\bmilestones?\b/i.test(t)) query.milestone = true;
  const atM = t.match(/\bat\s+(?:the\s+)?([a-zA-Z0-9' ]+?)(?:\s+(?:this|last|in)\b|[.?!]*$)/i);
  if (atM) query.place = normalizePlace(atM[1]);
  if (/\bthis\s+year\b/i.test(t)) {
    const y = new Date().getFullYear();
    query.from = `${y}-01-01`; query.to = `${y}-12-31`;
  } else if (/\blast\s+year\b/i.test(t)) {
    const y = new Date().getFullYear() - 1;
    query.from = `${y}-01-01`; query.to = `${y}-12-31`;
  } else {
    const yearM = t.match(/\b(20\d{2})\b/);
    if (yearM) { query.from = `${yearM[1]}-01-01`; query.to = `${yearM[1]}-12-31`; }
  }
  return query;
}

async function resolveMomentEdit(term: string, patch: MomentPatch, note?: string): Promise<One> {
  const rows = await findMoments(term);
  if (rows.length === 0) return { reply: "", action: { kind: "none", message: await momentsNotFound(term) } };
  if (rows.length > 1) {
    return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${term.trim()}" — be more specific:\n${pickList(rows)}` } };
  }
  const mo = rows[0];
  const label = note ? `"${mo.title || "that moment"}" (${note})` : `"${mo.title || "that moment"}"`;
  return { reply: "", action: { kind: "edit_moment", id: mo.id, patch, label } };
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
